import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GOLD_INK = "var(--brand-accent-ink)";
const TYPE_STYLE = {
  platform: "bg-slate-100 border-slate-300 text-slate-700",
  billing: "bg-teal-50 border-teal-200 text-teal-700",
  ai_gateway: "bg-blue-50 border-blue-200 text-blue-700",
  pii_reveal: "bg-red-50 border-red-200 text-red-700",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function RootTenantOverview({ subdomain, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!subdomain) { setData(null); return; }
    platformApi.get(`/tenants/${subdomain}/overview`)
      .then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [subdomain]);

  return (
    <Dialog open={!!subdomain} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="root-overview-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-bold text-[#1E293B] flex items-center gap-2">
            <Eye className="h-4 w-4" style={{ color: GOLD_INK }} />
            Tenant overview — <span className="font-mono">{subdomain}</span>
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
              read only
            </span>
          </DialogTitle>
        </DialogHeader>

        {!data ? <Skeleton className="h-64 w-full rounded-none" /> : data.error ? (
          <p className="text-[12px] text-red-600">Could not load tenant overview.</p>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
              {[
                ["Organization", data.tenant.org_name],
                ["Plan", `${data.tenant.plan}${data.tenant.employee_cap ? ` (cap ${data.tenant.employee_cap})` : ""}`],
                ["Status", data.tenant.status],
                ["Created", fmt(data.tenant.created_at)],
                ["Billing state", data.billing.state + (data.billing.comped ? " (comped)" : "")],
                ["Subscription", data.billing.subscription_id || "—"],
                ["Next billing", fmt(data.billing.next_billing_time)],
                ["Trial ends", fmt(data.tenant.trial_ends_at)],
              ].map(([label, value]) => (
                <div key={label} className="border border-slate-200 px-3 py-2">
                  <p className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-semibold">{label}</p>
                  <p className="font-mono text-[11px] text-[#1E293B] mt-0.5 break-all capitalize">{String(value)}</p>
                </div>
              ))}
            </div>

            <div data-testid="root-overview-kpis">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1E293B] mb-2">Usage</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
                {Object.entries(data.kpis).map(([k, v]) => (
                  <div key={k} className="border border-slate-100 py-2">
                    <p className="text-[15px] font-bold font-mono text-[#1E293B]">{v}</p>
                    <p className="text-[8px] uppercase tracking-wider text-slate-400 mt-0.5">{k.replace("_", " ")}</p>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="root-overview-cloud">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1E293B] mb-2">Cloud connections</p>
              {(data.cloud_adapters || []).length === 0 ? (
                <p className="text-[12px] text-slate-400">No enterprise database linked.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200">
                  {data.cloud_adapters.map((a) => (
                    <div key={a.id} className="px-3 py-2 flex items-center gap-3 text-[11px]">
                      <span className="font-semibold text-[#1E293B]">{a.provider_label}</span>
                      <span className="text-slate-500">{a.region_label}</span>
                      <span className={`text-[8px] font-mono uppercase px-1.5 py-0.5 border ${a.sovereign ? "bg-teal-50 border-teal-200 text-teal-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                        {a.sovereign ? "sovereign" : "cross-border"}
                      </span>
                      <span className="font-mono text-slate-400 ml-auto">{a.host_masked}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div data-testid="root-overview-activity">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#1E293B] mb-2">Recent activity</p>
              {data.recent_activity.length === 0 ? (
                <p className="text-[12px] text-slate-400">No recent activity.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200">
                  {data.recent_activity.map((a, i) => (
                    <div key={i} className="px-3 py-2 flex items-center gap-3 text-[11px]">
                      <span className="font-mono text-slate-400 whitespace-nowrap w-32 shrink-0">
                        {new Date(a.at).toLocaleString("en-GB")}
                      </span>
                      <span className={`text-[8px] font-mono uppercase px-1.5 py-0.5 border shrink-0 ${TYPE_STYLE[a.type] || "bg-slate-50 border-slate-200 text-slate-500"}`}>
                        {a.type.replace("_", " ")}
                      </span>
                      <span className="text-slate-600 truncate">{a.summary}</span>
                      <span className="font-mono text-slate-400 ml-auto shrink-0">{a.actor || ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
